import React, { useState } from 'react';
import axios from 'axios';
import { Wallet as WalletIcon, Copy, Send, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Wallet = () => {
  const [wallet, setWallet] = useState(null);
  const [balance, setBalance] = useState(null);
  const [sendForm, setSendForm] = useState({
    recipient: '',
    amount: '',
    tokenSymbol: 'NATIVE'
  });
  const [loading, setLoading] = useState(false);

  const createWallet = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/wallet/create`, {});
      setWallet(response.data);
      toast.success('Wallet created successfully!');
      checkBalance(response.data.address);
    } catch (error) {
      toast.error('Failed to create wallet');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const checkBalance = async (address = wallet?.address) => {
    if (!address) return;
    try {
      const response = await axios.post(`${API}/wallet/balance`, {
        address,
        token_symbol: sendForm.tokenSymbol
      });
      setBalance(response.data.balance);
    } catch (error) {
      toast.error('Failed to fetch balance');
      console.error(error);
    }
  };

  const sendTransaction = async () => {
    if (!wallet || !sendForm.recipient || !sendForm.amount) {
      toast.error('Please fill all fields');
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API}/transaction/create`, {
        sender: wallet.address,
        recipient: sendForm.recipient,
        amount: parseFloat(sendForm.amount),
        token_symbol: sendForm.tokenSymbol,
        private_key: wallet.private_key
      });
      toast.success('Transaction submitted! Mine a block to confirm.');
      setSendForm({ recipient: '', amount: '', tokenSymbol: 'NATIVE' });
      checkBalance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transaction failed');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard!');
      } else {
        // Fallback method for browsers without clipboard API
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          toast.success('Copied to clipboard!');
        } catch (err) {
          toast.error('Failed to copy');
        }
        document.body.removeChild(textArea);
      }
    } catch (err) {
      toast.error('Failed to copy to clipboard');
    }
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="font-unbounded text-4xl font-bold text-text-primary mb-2">
          Wallet Manager
        </h1>
        <p className="text-text-secondary">Create and manage your blockchain wallet</p>
      </div>

      {!wallet ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl glass p-12 rounded-sm text-center"
        >
          <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <WalletIcon size={40} className="text-primary" strokeWidth={1.5} />
          </div>
          <h2 className="font-unbounded text-2xl font-bold mb-4 text-text-primary">
            Create Your Wallet
          </h2>
          <p className="text-text-secondary mb-8">
            Get started by creating a new wallet with a unique address and private key
          </p>
          <button
            onClick={createWallet}
            disabled={loading}
            data-testid="create-wallet-btn"
            className="px-8 py-3 bg-primary text-primary-foreground font-bold rounded-sm hover:bg-primary-hover transition-colors duration-200 disabled:opacity-50"
          >
            {loading ? 'Creating...' : 'Create Wallet'}
          </button>
        </motion.div>
      ) : (
        <div className="space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass p-6 rounded-sm"
          >
            <h2 className="font-unbounded text-xl font-bold mb-4 text-text-primary">
              Wallet Information
            </h2>
            <div className="space-y-4">
              <div>
                <label className="text-text-muted text-sm mb-2 block">Address</label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 p-3 bg-background-subtle rounded-sm border border-border">
                    <p className="font-mono text-sm text-text-primary break-all">
                      {wallet.address}
                    </p>
                  </div>
                  <button
                    onClick={() => copyToClipboard(wallet.address)}
                    data-testid="copy-address-btn"
                    className="p-3 bg-background-subtle border border-border rounded-sm hover:border-primary/30 transition-colors duration-200"
                  >
                    <Copy size={18} className="text-text-secondary" />
                  </button>
                </div>
              </div>
              <div>
                <label className="text-text-muted text-sm mb-2 block">Private Key</label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 p-3 bg-background-subtle rounded-sm border border-border">
                    <p className="font-mono text-sm text-text-primary break-all">
                      {wallet.private_key}
                    </p>
                  </div>
                  <button
                    onClick={() => copyToClipboard(wallet.private_key)}
                    data-testid="copy-private-key-btn"
                    className="p-3 bg-background-subtle border border-border rounded-sm hover:border-primary/30 transition-colors duration-200"
                  >
                    <Copy size={18} className="text-text-secondary" />
                  </button>
                </div>
                <p className="text-xs text-accent-warning mt-2">
                  ⚠️ Keep your private key safe and never share it!
                </p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-text-muted text-sm">Balance</label>
                  <button
                    onClick={() => checkBalance()}
                    data-testid="refresh-balance-btn"
                    className="text-primary text-sm flex items-center gap-1 hover:text-primary-hover transition-colors duration-200"
                  >
                    <RefreshCw size={14} />
                    Refresh
                  </button>
                </div>
                <div className="p-4 bg-background-subtle rounded-sm border border-border">
                  <p className="font-unbounded text-3xl font-bold text-primary" data-testid="wallet-balance">
                    {balance !== null ? balance : '...'} {sendForm.tokenSymbol}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass p-6 rounded-sm"
          >
            <h2 className="font-unbounded text-xl font-bold mb-4 text-text-primary">
              Send Tokens
            </h2>
            <div className="space-y-4">
              <div>
                <label className="text-text-muted text-sm mb-2 block">Recipient Address</label>
                <input
                  type="text"
                  value={sendForm.recipient}
                  onChange={(e) => setSendForm({ ...sendForm, recipient: e.target.value })}
                  placeholder="Enter recipient address"
                  data-testid="recipient-input"
                  className="w-full p-3 bg-background-subtle border border-border rounded-sm text-text-primary focus:border-primary focus:outline-none font-mono text-sm"
                />
              </div>
              <div>
                <label className="text-text-muted text-sm mb-2 block">Amount</label>
                <input
                  type="number"
                  value={sendForm.amount}
                  onChange={(e) => setSendForm({ ...sendForm, amount: e.target.value })}
                  placeholder="0.00"
                  data-testid="amount-input"
                  className="w-full p-3 bg-background-subtle border border-border rounded-sm text-text-primary focus:border-primary focus:outline-none"
                />
              </div>
              <div>
                <label className="text-text-muted text-sm mb-2 block">Token</label>
                <select
                  value={sendForm.tokenSymbol}
                  onChange={(e) => {
                    setSendForm({ ...sendForm, tokenSymbol: e.target.value });
                    checkBalance();
                  }}
                  data-testid="token-select"
                  className="w-full p-3 bg-background-subtle border border-border rounded-sm text-text-primary focus:border-primary focus:outline-none"
                >
                  <option value="NATIVE">NATIVE</option>
                </select>
              </div>
              <button
                onClick={sendTransaction}
                disabled={loading}
                data-testid="send-transaction-btn"
                className="w-full py-3 bg-primary text-primary-foreground font-bold rounded-sm hover:bg-primary-hover transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Send size={18} />
                {loading ? 'Sending...' : 'Send Transaction'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Wallet;
